import type { iWACallLog } from "@sdk/wa-call-logs/wa-call-logs-model";

/**
 * Call statuses that represent an active, ringing, or initiating call
 * (not yet ended: COMPLETED / REJECTED).
 */
export const ACTIVE_CALL_STATUSES = [
  "INCOMING",
  "IN_PROGRESS",
  "OUTGOING_INIT",
  "OUTGOING_INIT_SUCCESS",
  "RINGING",
] as const;

export type WACallLogActiveStatus = (typeof ACTIVE_CALL_STATUSES)[number];

/**
 * Returns true if the call log is in an active/ringing/initiating state.
 * Used to decide whether to show live call UI vs "Call ended" in message list
 * and to pick the "focused" call in FloatingWhatsAppCallContainer.
 */
export function isWACallLogActive(
  callLog: iWACallLog | null | undefined,
): boolean {
  if (!callLog?.callStatus) return false;
  return (ACTIVE_CALL_STATUSES as readonly string[]).includes(
    callLog.callStatus,
  );
}

/** Default: treat incoming as "recent" if started within this many ms */
const INCOMING_RINGING_WINDOW_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Returns true if the call is incoming, not yet answered, and (optionally) within
 * the ringing window. Used for ringing audio and "incoming call" UI.
 */
export function isWACallLogIncomingRinging(
  callLog: iWACallLog | null | undefined,
  currentUserId: string,
  options?: { withinMs?: number },
): boolean {
  if (!callLog) return false;
  const withinMs = options?.withinMs ?? INCOMING_RINGING_WINDOW_MS;
  return (
    callLog.callStatus === "INCOMING" &&
    !!callLog.ringAgents?.includes(currentUserId) &&
    !callLog.answeredAt &&
    !callLog.endedAt &&
    callLog.startedAt > Date.now() - withinMs
  );
}
